/**
 * Custom A-Frame component for ROQ demo event handling
 * Handles MindAR targetFound and targetLost events with analytics integration
 * Requirements: 3.1, 3.2, 4.1, 4.2
 */

declare global {
  interface Window {
    AFRAME: any;
    app?: any;
  }
}

export function registerROQDemoController(): void {
  if (!window.AFRAME) {
    console.error('A-Frame not loaded - cannot register ROQ demo controller')
    return
  }

  // Prevent duplicate registration when AREngine is recreated (client switch)
  if ((window.AFRAME as any).components && (window.AFRAME as any).components['roq-demo-controller']) {
    console.log('ROQ Demo Controller already registered - skipping')
    return
  }

  window.AFRAME.registerComponent('roq-demo-controller', {
    schema: {
      targetIndex: { type: 'number', default: 0 },
      guidanceTimeout: { type: 'number', default: 5000 },
      analyticsEnabled: { type: 'boolean', default: true }
    },

    init: function() {
      console.log('ROQ Demo Controller initialized')
      
      // Bind event handlers
      this.onTargetFound = this.onTargetFound.bind(this)
      this.onTargetLost = this.onTargetLost.bind(this)
      this.onSceneLoaded = this.onSceneLoaded.bind(this)
      
      // Initialize state
      this.isTargetDetected = false
      this.guidanceDisplayed = false
      this.guidanceTimeout = null
      this.scanningStartTime = null
      
      // Debounce timers to prevent rapid duplicate events
      this.targetFoundDebounceTimer = null
      this.targetLostDebounceTimer = null
      
      // Always wait for scene to be fully loaded before setting up events
      // This ensures the DOM is ready and image target exists
      if (this.el.hasLoaded) {
        // If already loaded, wait briefly for DOM to settle
        setTimeout(() => {
          this.setupEventListeners()
        }, 50) // Reduced from 100ms for faster initialization
      } else {
        this.el.addEventListener('loaded', this.onSceneLoaded)
      }
    },

    onSceneLoaded: function() {
      console.log('ROQ Demo Controller: Scene loaded, setting up event listeners')
      // Wait briefly for DOM to settle after scene load
      setTimeout(() => {
        this.setupEventListeners()
      }, 50) // Reduced from 100ms for faster initialization
    },

    setupEventListeners: function() {
      // Find ALL image target entities with retry mechanism.
      // Multi-target collections create one entity per character.
      let attempts = 0
      const maxAttempts = 5
      const retryDelay = 50

      const trySetup = () => {
        const imageTargets = this.el.querySelectorAll('[mindar-image-target]')

        if (!imageTargets.length) {
          attempts++
          if (attempts < maxAttempts) {
            console.log(`ROQ Demo Controller: Image targets not found, retrying... (${attempts}/${maxAttempts})`)
            setTimeout(trySetup, retryDelay)
            return
          } else {
            console.error('ROQ Demo Controller: No MindAR image targets found after multiple attempts')
            return
          }
        }

        console.log(`ROQ Demo Controller: Found ${imageTargets.length} image target(s), attaching listeners`)

        // Store for cleanup
        this._imageTargets = Array.from(imageTargets)

        // Register listeners on every target entity
        this._imageTargets.forEach((imageTarget: Element) => {
          imageTarget.addEventListener('targetFound', this.onTargetFound)
          imageTarget.addEventListener('targetLost', this.onTargetLost)
        })

        // Start initial guidance timeout when scanning begins
        this.startGuidanceTimeout()
      }

      trySetup()
    },

    onTargetFound: function(event: any) {
      const targetIndex = event.detail?.targetIndex ?? 0
      
      // Debounce: Clear any pending lost event
      if (this.targetLostDebounceTimer) {
        clearTimeout(this.targetLostDebounceTimer)
        this.targetLostDebounceTimer = null
      }
      
      // Debounce: If already detected, ignore duplicate events
      if (this.isTargetDetected) {
        return
      }
      
      // Debounce: Small delay to prevent rapid duplicate events
      if (this.targetFoundDebounceTimer) {
        return // Already processing
      }
      
      this.targetFoundDebounceTimer = setTimeout(() => {
        this.targetFoundDebounceTimer = null
        
        console.log('🎯 ROQ Demo Controller: Target found!', { targetIndex })
        
        // Update detection state
        this.isTargetDetected = true
        this.guidanceDisplayed = false
        
        // Clear guidance timeout since target was found
        this.clearGuidanceTimeout()
        
        // Connect MindAR tracking events to analytics tracking
        if (this.data.analyticsEnabled && window.app) {
          try {
            // Fire analytics event for successful ROQ logo detection
            window.app.analyticsManager?.fireRoqScanSuccess()
            console.log('ROQ Demo Controller: Analytics event fired for target detection')
          } catch (error) {
            console.error('ROQ Demo Controller: Failed to fire analytics event:', error)
          }
        }
        
        // Hide guidance text since target was found
        this.hideGuidanceText()
        
        // Dispatch custom event — AREngine listens for this and calls
        // onMarkerFoundCallback(targetIndex) which routes to ApplicationController
        // with the correct targetIndex.  Do NOT call window.app.handleMarkerDetected()
        // directly here because it would default to targetIndex 0 and load the
        // wrong character before the correct callback fires.
        this.el.emit('roq-target-found', { targetIndex })
      }, 50) // 50ms debounce to prevent rapid duplicate events
    },

    onTargetLost: function(event: any) {
      const targetIndex = event.detail?.targetIndex ?? 0
      
      // Debounce: Clear any pending found event
      if (this.targetFoundDebounceTimer) {
        clearTimeout(this.targetFoundDebounceTimer)
        this.targetFoundDebounceTimer = null
      }
      
      // Debounce: If already lost, ignore duplicate events
      if (!this.isTargetDetected) {
        return
      }
      
      // Debounce: Small delay to prevent rapid duplicate events
      if (this.targetLostDebounceTimer) {
        return // Already processing
      }
      
      this.targetLostDebounceTimer = setTimeout(() => {
        this.targetLostDebounceTimer = null
        
        console.log('❌ ROQ Demo Controller: Target lost!', { targetIndex })
        
        // Update detection state
        this.isTargetDetected = false
        
        // Start guidance timeout for when logo is not found
        this.startGuidanceTimeout()
        
        // Notify application controller of marker loss
        if (window.app && typeof window.app.handleMarkerLost === 'function') {
          window.app.handleMarkerLost()
        }
        
        // Dispatch custom event for other components
        this.el.emit('roq-target-lost', { targetIndex })
      }, 100) // 100ms debounce (slightly longer for lost events to prevent flickering)
    },

    /**
     * Start 5-second timeout for guidance display when logo not found
     * Requirements: 3.1, 3.2
     */
    startGuidanceTimeout: function() {
      // Clear any existing timeout
      this.clearGuidanceTimeout()
      
      // Record scanning start time
      this.scanningStartTime = Date.now()
      
      console.log('ROQ Demo Controller: Starting guidance timeout (5 seconds)...')
      
      this.guidanceTimeout = setTimeout(() => {
        if (!this.isTargetDetected && !this.guidanceDisplayed) {
          console.log('⏰ ROQ Demo Controller: 5-second timeout reached - showing guidance')
          this.showGuidanceText()
          this.guidanceDisplayed = true
        }
      }, this.data.guidanceTimeout)
    },

    /**
     * Clear guidance timeout
     * Requirements: 3.2
     */
    clearGuidanceTimeout: function() {
      if (this.guidanceTimeout) {
        clearTimeout(this.guidanceTimeout)
        this.guidanceTimeout = null
        console.log('ROQ Demo Controller: Guidance timeout cleared')
      }
    },
    
    /**
     * Clear all debounce timers
     */
    clearDebounceTimers: function() {
      if (this.targetFoundDebounceTimer) {
        clearTimeout(this.targetFoundDebounceTimer)
        this.targetFoundDebounceTimer = null
      }
      if (this.targetLostDebounceTimer) {
        clearTimeout(this.targetLostDebounceTimer)
        this.targetLostDebounceTimer = null
      }
    },

    /**
     * Show guidance text overlay
     * Requirements: 3.2, 3.3
     */
    showGuidanceText: function() {
      console.log('ROQ Demo Controller: Showing guidance text')
      
      // Dispatch event to UI manager to show guidance
      // Default message matches brief: "Aim your camera at the design to begin"
      document.dispatchEvent(new CustomEvent('showGuidance', {
        detail: {
          message: 'Move camera slowly over flat prints or shirts until the design is detected',
          source: 'roq-demo-controller',
          timeout: true,
          scanningTime: this.scanningStartTime ? Date.now() - this.scanningStartTime : 0
        }
      }))
    },

    /**
     * Hide guidance text overlay
     * Requirements: 4.1, 4.2
     */
    hideGuidanceText: function() {
      console.log('ROQ Demo Controller: Hiding guidance text')
      
      // Dispatch event to UI manager to hide guidance
      document.dispatchEvent(new CustomEvent('hideGuidance', {
        detail: {
          source: 'roq-demo-controller',
          reason: 'target-found'
        }
      }))
    },

    /**
     * Get current detection state
     */
    getDetectionState: function() {
      return {
        isTargetDetected: this.isTargetDetected,
        guidanceDisplayed: this.guidanceDisplayed,
        scanningStartTime: this.scanningStartTime,
        scanningDuration: this.scanningStartTime ? Date.now() - this.scanningStartTime : 0
      }
    },

    /**
     * Reset detection state
     */
    resetDetectionState: function() {
      this.isTargetDetected = false
      this.guidanceDisplayed = false
      this.scanningStartTime = null
      this.clearGuidanceTimeout()
      this.clearDebounceTimers()
      console.log('ROQ Demo Controller: Detection state reset')
    },

    remove: function() {
      console.log('ROQ Demo Controller: Cleaning up')

      // Clear timeout and debounce timers
      this.clearGuidanceTimeout()
      this.clearDebounceTimers()

      // Remove event listeners from all target entities
      if (this._imageTargets) {
        this._imageTargets.forEach((imageTarget: Element) => {
          imageTarget.removeEventListener('targetFound', this.onTargetFound)
          imageTarget.removeEventListener('targetLost', this.onTargetLost)
        })
        this._imageTargets = null
      }

      this.el.removeEventListener('loaded', this.onSceneLoaded)
    }
  })

  console.log('ROQ Demo Controller component registered successfully')
}